import * as React from 'react'

import { observer } from 'mobx-react'

import MarkupContextToolbar from '~/client/src/shared/components/MapMarkups/MarkupContextToolbar'
import MarkupSubToolbar from '~/client/src/shared/components/MapMarkups/MarkupSubToolbar'
import MarkupTextContextToolbar from '~/client/src/shared/components/MapMarkups/MarkupTextContextToolbar'
import MarkupToolbar from '~/client/src/shared/components/MapMarkups/MarkupToolbar'

import type PhotoMarkupStore from './PhotoMarkup.store'
import PhotoMarkupFloatingToolbar from './PhotoMarkupFloatingToolbar'

interface IProps {
  store: PhotoMarkupStore
}

const noop = (): void => undefined

const PhotoMarkupContextToolbars = observer(
  function PhotoMarkupContextToolbars({ store }: IProps): JSX.Element {
    const { isMobileEnv, toolbarAnchor, selectedPath, selectedText } = store
    const stylePreview = isMobileEnv ? store.style.stylePreview : undefined
    if (!toolbarAnchor) return null

    return (
      <PhotoMarkupFloatingToolbar
        anchor={toolbarAnchor}
        stageSize={store.stageSize}
        isMobileEnv={isMobileEnv}
      >
        {selectedPath && (
          <MarkupContextToolbar
            kind={selectedPath.kind}
            color={selectedPath.color}
            strokeWidth={selectedPath.strokeWidth}
            strokeStyle={selectedPath.strokeStyle}
            fillColor={selectedPath.fillColor}
            fillStyle={selectedPath.fillStyle}
            arrowheadStart={selectedPath.arrowheadStart}
            arrowheadEnd={selectedPath.arrowheadEnd}
            isMobileEnv={isMobileEnv}
            stylePreview={stylePreview}
            onColorChange={store.style.changeSelectedColor}
            onStrokeWidthChange={store.style.changeSelectedWidth}
            onStrokeStyleChange={store.style.changeSelectedStrokeStyle}
            onFillColorChange={store.style.changeSelectedFillColor}
            onFillStyleChange={store.style.changeSelectedFillStyle}
            onArrowheadChange={store.style.changeSelectedArrowhead}
            onDelete={store.deleteSelected}
          />
        )}
        {selectedText && (
          <MarkupTextContextToolbar
            color={selectedText.color}
            fillColor={selectedText.fillColor}
            fontSize={selectedText.fontSize}
            bold={selectedText.bold}
            italic={selectedText.italic}
            underline={selectedText.underline}
            isMobileEnv={isMobileEnv}
            stylePreview={stylePreview}
            onColorChange={store.style.changeTextColor}
            onFillColorChange={store.style.changeTextFillColor}
            onFontSizeChange={store.style.changeTextFontSize}
            onToggleBold={store.style.toggleTextBold}
            onToggleItalic={store.style.toggleTextItalic}
            onToggleUnderline={store.style.toggleTextUnderline}
            onDelete={store.deleteSelected}
          />
        )}
      </PhotoMarkupFloatingToolbar>
    )
  },
)

function PhotoMarkupToolbars({ store }: IProps): JSX.Element {
  const { isMobileEnv } = store

  return (
    <div className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-20">
      <div className="tw:pointer-events-auto tw:absolute tw:bottom-3 tw:left-1/2 tw:-translate-x-1/2">
        <MarkupSubToolbar
          isMobileEnv={isMobileEnv}
          stylePreview={isMobileEnv ? store.style.stylePreview : undefined}
          activeTool={store.activeTool}
          color={store.style.color}
          strokeWidth={store.style.strokeWidth}
          shapeKind={store.shapeKind}
          lineType={store.lineType}
          drawType={store.drawType}
          fillColor={store.style.fillColor}
          fillStyle={store.style.fillStyle}
          onColorChange={store.style.changeColor}
          onStrokeWidthChange={store.style.changeStrokeWidth}
          onShapeKindChange={store.changeShapeKind}
          onLineTypeChange={store.changeLineType}
          onDrawTypeChange={store.changeDrawType}
          onResetToSelect={() => store.selectTool('select')}
          onFillColorChange={store.style.changeFillColor}
          onFillStyleChange={store.style.changeFillStyle}
          polygonDrawing={store.polygonDrawing}
          onFinishPolygon={store.finishPolygon}
          onCancelPolygon={store.cancelPolygon}
          polylineDrawing={store.polylineDrawing}
          onFinishPolyline={store.finishPolyline}
          onCancelPolyline={store.cancelPolyline}
        />
      </div>
      <PhotoMarkupContextToolbars store={store} />
    </div>
  )
}

export const PhotoMarkupMainToolbar = observer(function PhotoMarkupMainToolbar({
  store,
}: IProps): JSX.Element {
  return (
    <MarkupToolbar
      isMobileEnv={store.isMobileEnv}
      activeTool={store.activeTool}
      canUndo={store.history.canUndo}
      canRedo={store.history.canRedo}
      legendOpen={false}
      showLegendHint={false}
      hideLegend
      onSelectTool={store.selectTool}
      onUndo={store.undo}
      onRedo={store.redo}
      onLegendOpenChange={noop}
    />
  )
})

export default observer(PhotoMarkupToolbars)
